import { useEffect } from "react";

type JsonLd = Record<string, unknown>;

export type SeoProps = {
  title: string;
  description: string;
  keywords?: string;
  path?: string;
  image?: string;
  type?: string;
  jsonLd?: JsonLd | JsonLd[];
};

const MARK = "data-guada-seo";

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"][${MARK}]`,
  );
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    el.setAttribute(MARK, "true");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(
    `link[rel="${rel}"][${MARK}]`,
  );
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute(MARK, "true");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function Seo({
  title,
  description,
  keywords,
  path,
  image,
  type = "website",
  jsonLd,
}: SeoProps) {
  useEffect(() => {
    document.title = title;
    document.documentElement.lang = "zh-CN";

    const origin = window.location.origin;
    const canonical = `${origin}${path ?? window.location.pathname}`;

    upsertMeta("name", "description", description);
    if (keywords) {
      upsertMeta("name", "keywords", keywords);
    }
    upsertMeta("name", "robots", "index,follow");

    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:site_name", "诱惑吃瓜");
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", canonical);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);

    if (image) {
      upsertMeta("property", "og:image", image);
      upsertMeta("name", "twitter:image", image);
    }

    upsertLink("canonical", canonical);

    const scripts: HTMLScriptElement[] = [];
    if (jsonLd) {
      const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      blocks.forEach((block) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute(MARK, "true");
        script.textContent = JSON.stringify(block);
        document.head.appendChild(script);
        scripts.push(script);
      });
    }

    return () => {
      document
        .querySelectorAll(`[${MARK}="true"]`)
        .forEach((node) => node.parentNode?.removeChild(node));
      scripts.forEach((script) => script.parentNode?.removeChild(script));
    };
  }, [title, description, keywords, path, image, type, jsonLd]);

  return null;
}